import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play } from 'lucide-react';
import { getLocalizedText } from '../cms/localization';
import type { SupportedLang } from '../cms/localization';
import type { LocalizedText } from '../../shared/types';

interface ExpertSectionProps {
  content: Record<string, unknown>;
}

interface Expert {
  id: string;
  type: 'tcm' | 'pt';
  videoUrl: string;
  name: LocalizedText;
  title: LocalizedText;
  quote: LocalizedText;
}

const ExpertSection: React.FC<ExpertSectionProps> = ({ content }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as SupportedLang;

  const eyebrow = getLocalizedText(content.eyebrow, lang, t('experts.eyebrow'));
  const title = getLocalizedText(content.title, lang, t('experts.title'));
  const tcmLabel = getLocalizedText(content.tcmLabel, lang, t('experts.tcm'));
  const ptLabel = getLocalizedText(content.ptLabel, lang, t('experts.pt'));
  const recommendText = getLocalizedText(content.recommendText, lang, t('experts.recommend'));
  const experts = (content.experts as Expert[] | undefined) || [];

  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  return (
    <section id="experts" className="relative py-12 md:py-16 bg-brand-ink text-brand-paper overflow-hidden">
      <div className="absolute inset-0 bg-cloud opacity-[0.08] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-brand-gold font-sans tracking-[0.25em] text-xs uppercase block mb-4">
            {eyebrow}
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-brand-paper">
            {title}
          </h2>
          <div className="flex items-center justify-center gap-6 mt-6 text-sm font-sans text-brand-paper/60">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              {tcmLabel}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-400" />
              {ptLabel}
            </span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {experts.map((expert, index) => {
            const isTcm = expert.type === 'tcm';
            const name = getLocalizedText(expert.name, lang, t(`experts.list.${expert.id}.name`));
            const titleText = getLocalizedText(expert.title, lang, t(`experts.list.${expert.id}.title`));
            const quote = getLocalizedText(expert.quote, lang, t(`experts.list.${expert.id}.quote`));

            return (
              <motion.div
                key={expert.id}
                className="group relative bg-brand-charcoal/50 border border-brand-paper/10 overflow-hidden"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                {/* Video thumbnail area */}
                <div
                  className="relative aspect-[9/16] max-h-[480px] bg-brand-ink overflow-hidden cursor-pointer"
                  onClick={() => setActiveVideo(expert.videoUrl)}
                >
                  <video
                    src={expert.videoUrl}
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity pointer-events-none"
                    muted
                    playsInline
                    webkit-playsinline="true"
                    preload="metadata"
                    controls={false}
                  />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-16 h-16 rounded-full bg-brand-cinnabar/90 text-brand-paper flex items-center justify-center group-hover:bg-brand-cinnabar transition-colors shadow-[0_0_30px_rgba(var(--brand-cinnabar-rgb),0.5)]">
                      <Play className="w-6 h-6 ml-1" />
                    </div>
                  </div>
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 text-xs font-sans tracking-wider uppercase ${isTcm ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-blue-400/20 text-blue-200 border border-blue-400/30'}`}>
                      {isTcm ? tcmLabel : ptLabel}
                    </span>
                  </div>
                </div>

                {/* Quote */}
                <div className="p-6 md:p-8">
                  <p className="text-brand-paper/80 font-sans text-sm md:text-base leading-relaxed mb-4 italic">
                    "{quote}"
                  </p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-brand-paper font-serif">{name}</p>
                      <p className="text-brand-paper/50 text-xs font-sans">{titleText}</p>
                    </div>
                    <motion.a
                      href="#products"
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.97 }}
                      className="px-4 py-2 rounded-full bg-brand-gold/10 border border-brand-gold/30 text-brand-gold text-xs font-sans tracking-widest hover:bg-brand-gold hover:text-brand-ink transition-colors"
                    >
                      {recommendText}
                    </motion.a>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Video Modal */}
      <AnimatePresence>
        {activeVideo && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveVideo(null)}
          >
            <div className="relative w-full max-w-lg aspect-[9/16]">
              <button
                onClick={() => setActiveVideo(null)}
                className="absolute -top-12 right-0 text-brand-paper/80 hover:text-brand-paper"
              >
                <X className="w-6 h-6" />
              </button>
              <video
                src={activeVideo}
                controls
                autoPlay
                className="w-full h-full rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ExpertSection;
